import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import { ReplaceProduct } from "@coworker/app/src/components/RoomSettings/Organisms/ReplaceProduct";
import React from "react";
import {
  MainAlternativeProducts,
  Media,
  MediaResponse,
} from "@coworker/app/src/components/RoomSettings/types/media";
import { isMainProduct } from "@coworker/app/src/components/RoomSettings/Molecules/MediaArticles.helper";
import {
  doesSupportMainAndBackup,
  getMainAndAlternativeProducts,
  setMainAndBackupProduct,
} from "@coworker/app/src/components/RoomSettings/tools";
import { isCombinationArticleValid } from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import {
  Article,
  Articles,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { fetchItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { useUpdateMedia } from "@coworker/app/src/components/RoomSettings/hooks/useUpdateMedia";
import { useTranslation } from "@coworker/locales";
import { queryClient } from "../../../../../../src";

interface MainBackupItemSelectorProps {
  mediaResponse: MediaResponse;
  mediaId: string;
  article: Article;
  allArticles: Articles;
  combinationArticle?: unknown;
}

export const MainBackupItemSelector = ({
  mediaResponse,
  mediaId,
  allArticles,
  article,
  combinationArticle,
}: MainBackupItemSelectorProps) => {
  const { t } = useTranslation();
  const updateMedia = useUpdateMedia(mediaId || "");
  const mainAndAlternativeProduct = getMainAndAlternativeProducts(
    mediaResponse.media
  );
  const isMainProductChecked = isMainProduct(
    article?.id || "",
    mainAndAlternativeProduct.mainProductId1,
    mainAndAlternativeProduct.mainProductId2
  );

  const [mainProductChecked, setMainProductChecked] =
    React.useState(isMainProductChecked);

  const getCurrentAlternativeItemNo = () => {
    const {
      mainProductId1,
      alternativeProductId1,
      mainProductId2,
      alternativeProductId2,
    } = mediaResponse.media;
    return article?.id === mainProductId1
      ? alternativeProductId1
      : article?.id === mainProductId2
        ? alternativeProductId2
        : "";
  };

  const alternativeBackUpItemNo = getCurrentAlternativeItemNo();
  const isFirstMainProduct =
    article?.id === mediaResponse?.media.mainProductId1;

  const handleBackupItemChange = async (itemNo: string) => {
    const itemInfo = await queryClient.ensureQueryData({
      queryKey: ["item", "info", itemNo],
      queryFn: () => fetchItemInfo(itemNo),
    });
    if (!itemInfo) {
      return;
    }
    const updatedMedia = {
      ...mediaResponse?.media,
      ...(isFirstMainProduct
        ? {
            alternativeProductId1: itemInfo?.no,
            alternativeProductName1: itemInfo?.name,
          }
        : {
            alternativeProductId2: itemInfo.no,
            alternativeProductName2: itemInfo.name,
          }),
    };
    updateMedia.mutate(updatedMedia);
  };

  async function ToggleMainProduct() {
    if (!mediaResponse || !article) {
      return;
    }
    let data: MainAlternativeProducts = {
      ...mediaResponse.media,
      mainProductId1: "",
      alternativeProductId1: "",
      mainProductName1: "",
      alternativeProductName1: "",
      mainProductId2: "",
      alternativeProductId2: "",
      mainProductName2: "",
      alternativeProductName2: "",
    };

    data = setMainAndBackupProduct(
      data,
      isMainProductChecked,
      mediaResponse.media,
      article
    );

    setMainProductChecked(!mainProductChecked);
    updateMedia.mutate(data as Media);
  }

  const articleIds: string[] = allArticles?.map((article) => article.id) ?? [];
  const showBackupProducts = mainProductChecked;

  const isNonCompactMediaWithoutCombination =
    doesSupportMainAndBackup(mediaResponse?.media?.mediaType as MediaTypes) &&
    !isCombinationArticleValid(combinationArticle);
  return (
    <>
      {isNonCompactMediaWithoutCombination && (
        <FixaListViewItem
          title={t("mainProductBasicString")}
          emphasised={true}
          inset={true}
          control="switch"
          controlProps={{
            checked: mainProductChecked ?? false,
            value: "mainArticle",
          }}
          onChange={async () => {
            await ToggleMainProduct();
          }}
        />
      )}

      {showBackupProducts &&
        doesSupportMainAndBackup(
          mediaResponse.media.mediaType as MediaTypes
        ) && (
          <ReplaceProduct
            oldProductArticleIds={articleIds}
            numberBackup={
              article?.id === mediaResponse.media.mainProductId1 ? 1 : 2
            }
            handleItemChange={handleBackupItemChange}
            alternativeBackupItemNo={alternativeBackUpItemNo || ""}
            isSetAsBackupProduct={true}
          />
        )}
    </>
  );
};
